import { Node } from "@tiptap/core"
import { TextSelection } from "@tiptap/pm/state"

import type { FieldAttributes } from "./fields-html"
import type { CommandProps } from "@tiptap/core"
import type { DOMOutputSpec } from "@tiptap/pm/model"
import { RESPONSE_FIELD_NODE } from "./fields"
import {
	DEFAULT_EXPANDABLE_TITLE,
	emptyFieldAttributes,
	expandableSpec,
} from "./fields-html"

declare module "@tiptap/core" {
	interface Commands<ReturnType> {
		expandable: {
			insertExpandable: (options?: {
				title?: string
				child?: Partial<FieldAttributes>
			}) => ReturnType
		}
	}
}

export const EXPANDABLE_NODE = "expandable"

export const ExpandableExtension = Node.create({
	name: EXPANDABLE_NODE,
	group: "block",
	content: "block+",
	defining: true,
	isolating: true,
	// Ahead of the accordion's own `details` rule.
	priority: 110,

	addAttributes() {
		return {
			title: {
				default: DEFAULT_EXPANDABLE_TITLE,
				parseHTML: (element: HTMLElement) =>
					element.getAttribute("data-title") ?? DEFAULT_EXPANDABLE_TITLE,
				rendered: false,
			},
			defaultOpen: {
				default: false,
				parseHTML: (element: HTMLElement) => element.hasAttribute("open"),
				rendered: false,
			},
		}
	},

	parseHTML() {
		return [
			{
				tag: 'details[data-type="expandable"]',
				contentElement: (dom: globalThis.Node) =>
					(Array.from((dom as HTMLElement).children).find((child) =>
						child.classList.contains("pl-expandable-content"),
					) as HTMLElement | undefined) ?? (dom as HTMLElement),
			},
		]
	},

	renderHTML({ node }) {
		const title =
			typeof node.attrs.title === "string" && node.attrs.title.trim()
				? node.attrs.title
				: DEFAULT_EXPANDABLE_TITLE
		return expandableSpec(
			title,
			node.attrs.defaultOpen === true,
		) as DOMOutputSpec
	},

	addCommands() {
		return {
			// Mintlify nests an expandable inside a field, holding the child
			// fields, so it starts with one response field to fill in.
			insertExpandable:
				(options = {}) =>
				({ chain, state }: CommandProps) => {
					const from = state.selection.from
					return chain()
						.insertContent({
							type: this.name,
							attrs: { title: options.title ?? DEFAULT_EXPANDABLE_TITLE },
							content: [
								{
									type: RESPONSE_FIELD_NODE,
									attrs: { ...emptyFieldAttributes("response", options.child) },
									content: [{ type: "paragraph" }],
								},
							],
						})
						.command(({ tr, dispatch }) => {
							if (!dispatch) return true
							let target = -1
							tr.doc.descendants((child, pos) => {
								if (target >= 0) return false
								if (
									child.type.name === RESPONSE_FIELD_NODE &&
									pos >= from - 1
								) {
									target = pos
									return false
								}
								return true
							})
							if (target >= 0) {
								tr.setSelection(TextSelection.near(tr.doc.resolve(target + 2)))
							}
							return true
						})
						.run()
				},
		}
	},
})

export const ExpandableExtensions = [ExpandableExtension]
